/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * The access vocabulary, mirroring the backend's `AppRole`, `Workspace` and `AccessStatus`.
 *
 * <p>Deliberately the same strings the API enforces on rather than a translated copy: the UI hides
 * what the backend would refuse, and the two can only stay in agreement if they are saying the
 * same words. Display names come from the workspace catalogue, not from here.
 */

/** Privilege tier. Hierarchical — see {@link roleAtLeast}. */
export type AppRole = 'USER' | 'ADMIN' | 'SUPER_ADMIN';

/** Product area a user can be granted. */
export type WorkspaceId = 'MANUFACTURING' | 'ENGINEERING' | 'ANALYTICS' | 'ADMIN';

/** Where a signed-in caller stands with the application. */
export type AccessStatus = 'NOT_PROVISIONED' | 'DISABLED' | 'ACTIVE';

/** Weakest first, so index order is the hierarchy. Mirrors the backend enum's declaration order. */
export const ROLE_ORDER: AppRole[] = ['USER', 'ADMIN', 'SUPER_ADMIN'];

/** Whether a tier subsumes another. `roleAtLeast('SUPER_ADMIN', 'ADMIN')` is true. */
export function roleAtLeast(role: AppRole | null | undefined, floor: AppRole): boolean {
  if (!role) return false;
  return ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(floor);
}

/** `GET /access/me` — who the caller is and what they may reach. */
export interface CurrentUser {
  gmin: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  /** Null when the caller is not provisioned. */
  role: AppRole | null;
  status: AccessStatus;
  /** Already expanded by the backend, so a super admin arrives holding every workspace. */
  workspaces: WorkspaceId[];
}

/** `GET /access/workspaces` — how each workspace presents itself. */
export interface WorkspaceDefinition {
  id: WorkspaceId;
  displayName: string;
  description: string | null;
  /** False for Analytics and Admin: an admin grants them, a user never asks for them. */
  requestable: boolean;
}

/** Where a request stands. Mirrors the backend's `AccessRequestStatus`. */
export type AccessRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** A request to be let in, and what an admin decided about it. */
export interface AccessRequest {
  id: number;
  gmin: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  requestedWorkspaces: WorkspaceId[];
  justification: string | null;
  status: AccessRequestStatus;
  createdAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
}

/**
 * What the Request Access form sends. Nothing identifying: the backend takes the GMIN, name and
 * email off the token, so the form has no field for them and could not be believed if it did.
 */
export interface AccessRequestSubmission {
  workspaces: WorkspaceId[];
  justification?: string;
}

/** Full name as the admin panel and greetings render it, falling back to the GMIN. */
export function displayName(user: Pick<CurrentUser, 'firstName' | 'lastName' | 'gmin'>): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.gmin;
}

/** Initials for the avatar chip, e.g. "Sri Kamma" → "SK". */
export function initials(user: Pick<CurrentUser, 'firstName' | 'lastName' | 'gmin'>): string {
  // Trim before testing for a name: a directory record whose first name is a single space has
  // nothing to abbreviate, and should fall back to the GMIN like an absent one.
  const letters = [user.firstName, user.lastName]
    .map((part) => part?.trim().charAt(0) ?? '')
    .join('')
    .toUpperCase();
  return letters || user.gmin.slice(0, 2).toUpperCase();
}
